/**
 * Reading the Magic Email reply library from the wizard, loading the seed, and the list's search
 * and status filter. Changes to a reply live in `use-reply-editor`.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  wizardMagicEmailApi,
  wizardMagicEmailKeys,
  type ReplyListEntry,
  type ReplyStatus,
} from '../api/api';

export const errorText = (error: unknown) =>
  error instanceof Error ? error.message : 'Unknown error';

export function useReplyLibrary() {
  return useQuery({
    queryKey: wizardMagicEmailKeys.library(),
    queryFn: wizardMagicEmailApi.fetchLibrary,
  });
}

/** Labels, conditions and next steps: fixed in code, so read once. */
export function useReplyCatalog() {
  return useQuery({
    queryKey: wizardMagicEmailKeys.catalog(),
    queryFn: wizardMagicEmailApi.fetchCatalog,
    staleTime: Infinity,
  });
}

export function useImportLibrarySeed() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: wizardMagicEmailApi.importSeed,
    onSuccess: ({ added }) => {
      toast.success(`${added} replies loaded`);
      void queryClient.invalidateQueries({ queryKey: wizardMagicEmailKeys.all });
    },
    onError: error => toast.error(`Could not load the replies: ${errorText(error)}`),
  });
}

/** Publishes and switches on every reply with nothing in its way. */
export function usePublishAllReady() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: wizardMagicEmailApi.publishAll,
    onSuccess: ({ published, switchedOn }) => {
      const count = published + switchedOn;
      toast.success(count === 1 ? '1 reply is now live' : `${count} replies are now live`);
      void queryClient.invalidateQueries({ queryKey: wizardMagicEmailKeys.all });
    },
    onError: error => toast.error(`Not published: ${errorText(error)}`),
  });
}

/** The words still to write in (the agency's name, a support link…), and filling one in everywhere. */
export function usePlaceholders() {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: wizardMagicEmailKeys.placeholders(), queryFn: wizardMagicEmailApi.fetchPlaceholders });
  const fill = useMutation({
    mutationFn: wizardMagicEmailApi.fillPlaceholder,
    onSuccess: ({ updated }) => {
      toast.success(updated.length === 1 ? 'Written into 1 reply as a draft' : `Written into ${updated.length} replies as drafts`);
      void queryClient.invalidateQueries({ queryKey: wizardMagicEmailKeys.all });
    },
    onError: error => toast.error(`Not filled in: ${errorText(error)}`),
  });
  return { query, fill };
}

/** Replies that "Publish all ready" would publish or switch on. */
export const readyToPublish = (entries: ReplyListEntry[] | undefined) =>
  (entries ?? []).filter(entry => entry.problems.length === 0 && (entry.status !== 'live' || entry.hasUnpublishedChanges));

export type ReplyFilter = 'all' | ReplyStatus;

/** The list narrowed by status and by a search over title, topic and words, grouped by topic. */
export function useReplyListFilters(entries: ReplyListEntry[] | undefined) {
  const [status, setStatus] = useState<ReplyFilter>('all');
  const [search, setSearch] = useState('');

  const counts = useMemo(
    () => ({
      live: (entries ?? []).filter(entry => entry.status === 'live').length,
      draft: (entries ?? []).filter(entry => entry.status === 'draft').length,
      unavailable: (entries ?? []).filter(entry => entry.status === 'unavailable').length,
    }),
    [entries],
  );

  const groups = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const shown = (entries ?? []).filter(
      entry =>
        (status === 'all' || entry.status === status) &&
        (!needle ||
          [entry.title, entry.category, entry.summary].some(value =>
            value.toLowerCase().includes(needle),
          )),
    );
    const byCategory = new Map<string, ReplyListEntry[]>();
    for (const entry of shown)
      byCategory.set(entry.category, [...(byCategory.get(entry.category) ?? []), entry]);
    return [...byCategory.entries()].map(([category, replies]) => ({ category, replies }));
  }, [entries, search, status]);

  return { status, setStatus, search, setSearch, counts, groups };
}
